// src/components/DataTable.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  Container,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
} from '@mui/material';
import {
  getCompanySuppliersAction,
  getOrderBookingPaginatedAction,
} from './Action';

import moment from 'moment';
import {
  getOrderBookingFinalStatusById,
  getRGBAColor,
  orderBookingFinalStatusEnum,
  SupplierModuleTypeEnum,
} from '../../utils/settings';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch } from 'react-redux';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function PurchaseOrderList() {
  const location = useLocation();
  const companyId = location.state?.companyId;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBookingsList, setOrderBookingsList] = useState([]);
  const [companiesList, setCompaniesList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const dispatch = useDispatch();
  const [startEndDate, setStartEndDate] = useState({
    startDate: moment(moment().subtract(30, 'days')).format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
  });

  const [company, setCompany] = useState(companyId || null);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };
  const getCompany = async () => {
    const { data, error, message } = await getCompanySuppliersAction(
      SupplierModuleTypeEnum.po_history_enabled,
    );

    if (!error) {
      let list = [];
      data.map((industry) => {
        industry.suppliers_list.map((supplier) => {
          list.push({
            id: supplier.id,
            name: supplier.name,
          });
        });
      });

      setCompaniesList(list);
    } else {
      toast.error(message);
    }
  };
  const orderPaginated = async () => {
    const config = new URLSearchParams();
    config.append('f', 'id$gte$0');
    config.append('o', 'id$desc');
    config.append('page', page + 1);
    config.append('page_size', rowsPerPage);
    config.append('is_buyer', true);

    if (startEndDate.startDate && startEndDate.endDate) {
      config.append(
        'f',
        `documented_at$gte$${moment(startEndDate.startDate)
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ssZ')}`,
      );

      config.append(
        'f',
        `documented_at$lte$${moment(startEndDate.endDate)
          .endOf('day')
          .format('YYYY-MM-DD HH:mm:ssZ')}`,
      );
    }
    if (company) {
      config.append('f', `seller_id$eq$${company}`);
    }
    if (status) {
      config.append('f', `final_status$eq$${status}`);
    }

    const { data, error, message } = await getOrderBookingPaginatedAction(
      config,
    );

    if (!error) {
      setOrderBookingsList(data);
    } else {
      return toast.error(message);
    }
  };

  const getStatusList = () => {
    let list = [];
    orderBookingFinalStatusEnum.map((item) => {
      list.push({
        id: item.id,
        name: item.name,
      });
    });
    setStatusList(list);
  };

  useEffect(() => {
    orderPaginated({});
    getCompany();
    getStatusList();
  }, []);

  useEffect(() => {
    if (company || status || startEndDate) {
      orderPaginated();
    }
  }, [company, status, startEndDate, rowsPerPage, page]);
  const handleClearAllFilters = () => {
    setCompany(null);
    setStatus(null);
    setStartEndDate({
      startDate: moment(moment().subtract(30, 'days')).format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
    });
  };

  return (
    <>
      <Container
        maxWidth='true'
        sx={{
          pl: '2rem',
          pt: '2rem',
          height: window.innerHeight,
          overflowY: 'auto',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography
          variant='h5'
          style={{ marginBottom: '2rem', fontWeight: 'bold' }}
        >
          Purchase Orders
        </Typography>

        <Grid
          container
          spacing={2}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Grid
            item
            xs={12}
            lg={8}
          >
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
              >
                <TextField
                  fullWidth
                  label='Start Date'
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  value={startEndDate.startDate}
                  onChange={(e) =>
                    setStartEndDate({
                      ...startEndDate,
                      startDate: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
              >
                <TextField
                  fullWidth
                  label='End Date'
                  type='date'
                  InputLabelProps={{ shrink: true }}
                  value={startEndDate.endDate}
                  onChange={(e) =>
                    setStartEndDate({
                      ...startEndDate,
                      endDate: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
              >
                <FormControl fullWidth>
                  <InputLabel>Select Company</InputLabel>
                  <Select
                    value={company}
                    label='Select Company'
                    onChange={(e) => setCompany(e.target.value)}
                  >
                    {companiesList?.map((company) => (
                      <MenuItem
                        value={company.id}
                        key={company.id}
                      >
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
              >
                <FormControl fullWidth>
                  <InputLabel>Select Status</InputLabel>
                  <Select
                    value={status}
                    label='Select Status'
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    {statusList?.map((status) => (
                      <MenuItem
                        value={status.id}
                        key={status.id}
                      >
                        {status.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            lg={4}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'end', height: '100%' }}
            >
              <Button
                variant='contained'
                color='error'
                onClick={handleClearAllFilters}
                endIcon={<RestartAltIcon />}
              >
                Clear All
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => navigate('/order-booking/po-select-industry')}
                sx={{ ml: 2 }}
              >
                Create New PO
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>#</TableCell>
                  <TableCell align='center'>
                    <Box>
                      User PO No
                      <Box sx={{ fontSize: '10px', color: 'gray' }}>
                        Booking Price
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align='center'>Date</TableCell>
                  {/* <TableCell>UOM</TableCell> */}
                  <TableCell align='center'>Status</TableCell>
                  <TableCell align='center'>Ordered Qty</TableCell>
                  <TableCell align='center'>Delivered Qty</TableCell>
                  <TableCell align='center'>Pending Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderBookingsList.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align='center'
                    >
                      <Typography sx={{ color: 'red' }}>
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {orderBookingsList.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      navigate('/order-booking/po-details', {
                        state: {
                          orderID: row.booking_id,
                          companyID: row.seller_id,
                        },
                      });
                      dispatch({
                        type: 'IS_SECONDARY_ORDER',
                        data: false,
                      });
                    }}
                  >
                    <TableCell align='center'>{index + 1}</TableCell>
                    <TableCell align='center'>
                      <Box>
                        {row.user_booking_id ? row.user_booking_id : '-'}
                        <Box sx={{ fontSize: 'small', color: 'gray' }}>
                          {' '}
                          ₹ {row.booking_details[0].booking_price}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align='center'>
                      {moment(row?.date).format('DD MMM YYYY')}
                    </TableCell>

                    <TableCell
                      sx={{
                        width: '10rem',
                      }}
                      align='center'
                    >
                      <Box
                        sx={{
                          borderRadius: '10px',

                          textAlign: 'center',
                          backgroundColor: getRGBAColor(
                            getOrderBookingFinalStatusById(row?.final_status)
                              ?.color,
                          ),
                        }}
                      >
                        <Typography
                          sx={{
                            color: getOrderBookingFinalStatusById(
                              row?.final_status,
                            ).color,
                          }}
                        >
                          {
                            getOrderBookingFinalStatusById(row?.final_status)
                              ?.name
                          }
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align='center'>{`${Number(
                      row.booking_details[0].booking_quantity
                        .quantity_in_metric_ton,
                    ).toFixed(2)} ${
                      row.booking_details[0].booking_quantity.unit
                    }`}</TableCell>
                    <TableCell align='center'>{`${Number(
                      row.booking_details[0].delivered_quantity
                        .quantity_in_metric_ton,
                    ).toFixed(2)} ${
                      row.booking_details[0].booking_quantity.unit
                    }`}</TableCell>
                    <TableCell align='center'>{`${Number(
                      row.booking_details[0].remaining_quantity
                        .quantity_in_metric_ton,
                    ).toFixed(2)} ${
                      row.booking_details[0].booking_quantity.unit
                    }`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component='div'
            count={100}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count, page }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        </Paper>
      </Container>
    </>
  );
}
